import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { fetchMembers } from '@/features/members/api';
import { createNgRule } from '@/features/seating/api';
import { ApiError } from '@/lib/api/http';

/**
 * サクセス担当による席次NG申請（implementation-spec §9: 「サクセスは機密理由付きの
 * 追加申請を行える」）。一覧閲覧は事務局のみのため、このページは申請フォームのみ。
 */
export function NgRuleRequestPage(): ReactElement {
  const membersQuery = useQuery({
    queryKey: ['members-picker'],
    queryFn: () => fetchMembers({ per_page: 100 }),
  });

  const [memberAId, setMemberAId] = useState('');
  const [memberBId, setMemberBId] = useState('');
  const [reasonCategory, setReasonCategory] = useState('');
  const [confidentialReason, setConfidentialReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    setSuccess(false);
    try {
      await createNgRule({
        member_a_id: memberAId,
        member_b_id: memberBId,
        reason_category: reasonCategory,
        confidential_reason: confidentialReason || null,
      });
      setMemberAId('');
      setMemberBId('');
      setReasonCategory('');
      setConfidentialReason('');
      setSuccess(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '申請に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="席次NGの申請" description="事務局の承認後に有効になります。" />
      <MetaBox title="申請内容">
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}
        {success && (
          <Notice kind="success" dismissible={false}>
            申請しました。事務局の承認をお待ちください。
          </Notice>
        )}
        {membersQuery.isError && (
          <Notice kind="error" dismissible={false}>
            会員一覧の取得に失敗しました。
            <button
              type="button"
              onClick={() => void membersQuery.refetch()}
              className="ml-2 underline"
            >
              再試行
            </button>
          </Notice>
        )}

        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">会員A</span>
          <select
            required
            value={memberAId}
            onChange={(e) => setMemberAId(e.target.value)}
            className="min-w-[16rem] rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">選択してください</option>
            {(membersQuery.data?.items ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}（{m.member_id}）
              </option>
            ))}
          </select>
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">会員B</span>
          <select
            required
            value={memberBId}
            onChange={(e) => setMemberBId(e.target.value)}
            className="min-w-[16rem] rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">選択してください</option>
            {(membersQuery.data?.items ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}（{m.member_id}）
              </option>
            ))}
          </select>
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">区分</span>
          <input
            required
            value={reasonCategory}
            onChange={(e) => setReasonCategory(e.target.value)}
            placeholder="例: 面談で把握した配慮事項"
            className="w-full max-w-md rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="mb-3 block">
          <span className="mb-1.5 block text-wptext-muted">理由（機密・事務局のみ閲覧）</span>
          <textarea
            value={confidentialReason}
            onChange={(e) => setConfidentialReason(e.target.value)}
            rows={3}
            className="w-full max-w-md rounded-sm border border-wpborder px-3 py-2"
          />
        </label>

        <button
          type="button"
          disabled={busy || !memberAId || !memberBId || !reasonCategory}
          onClick={() => void onSubmit()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          申請する
        </button>
      </MetaBox>
    </>
  );
}
